import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { MessageList } from "@/components/MessageList";
import { MessageComposer } from "@/components/MessageComposer";
import { useRoomMessages } from "@/lib/messages";
import { useSession } from "@/lib/auth";
import { isIntegrationMessage } from "@/lib/message-sender";
import type { User } from "@/lib/types";
import { apiPost } from "@/lib/api";

export function ThreadPane({
  roomId,
  threadRootId,
  members,
  onClose,
  onJump,
  highlightMessageId,
}: {
  roomId: string;
  threadRootId: string;
  members: User[];
  onClose: () => void;
  onJump?: (messageId: string) => void;
  highlightMessageId?: string | null;
}): JSX.Element {
  const session = useSession();
  const chat = useRoomMessages(roomId, { threadRootId });
  const messages = chat.messages.map((m) => ({
    ...m,
    authorName: isIntegrationMessage(m)
      ? (m.sender?.display_name || m.authorName)
      : (members.find((u) => u.id === m.authorId)?.displayName || m.authorName),
  }));
  const root = messages.find((m) => m.id === threadRootId);
  const [replyingTo, setReplyingTo] = useState<{ id: string; author: string; preview: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const perform = (work: Promise<unknown>) => {
    setActionError(null);
    void work.catch((e) => setActionError(e instanceof Error ? e.message : String(e)));
  };
  useEffect(() => {
    setReplyingTo(null);
    setActionError(null);
    void chat.loadMessage(threadRootId).then((message) => {
      if (!message || message.id !== threadRootId) setActionError("スレッドの元メッセージを開けません。");
    }).catch((error) => setActionError(error instanceof Error ? error.message : "スレッドの元メッセージを開けません。"));
  }, [threadRootId, chat.loadMessage]);
  useEffect(() => {
    if (!highlightMessageId || highlightMessageId === threadRootId) return;
    void chat.loadMessage(highlightMessageId).catch(() => undefined);
  }, [highlightMessageId, threadRootId, chat.loadMessage]);
  useEffect(() => {
    if (!highlightMessageId) return;
    const element = Array.from(document.querySelectorAll<HTMLElement>("[data-thread-root] [data-message-id]")).find(
      (el) => el.dataset.messageId === highlightMessageId,
    );
    element?.scrollIntoView?.({ block: "center" });
  }, [highlightMessageId, chat.messages]);
  const onReply = useCallback(
    (mid: string) => {
      const m = messages.find((mm) => mm.id === mid);
      if (!m) return;
      setReplyingTo({ id: mid, author: m.authorName || m.authorId, preview: m.body.slice(0, 80) });
    },
    [messages],
  );
  return (
    <aside
      data-thread-root={threadRootId}
      className="w-full @min-[48rem]:w-[24rem] shrink-0 @min-[48rem]:border-l border-border-subtle bg-canvas flex flex-col min-h-0"
      aria-label="スレッド"
    >
      <header className="h-14 px-3 flex items-center gap-2 border-b border-border-subtle">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold truncate">スレッド</h2>
          <p className="text-xs text-fg-muted truncate">
            {root ? `${root.authorName || root.authorId}: ${root.body.slice(0, 60)}` : chat.loading ? "返信を読み込み中…" : "スレッドを表示できません"}
          </p>
        </div>
        <button type="button" aria-label="スレッドを閉じる" className="p-1.5 rounded hover:bg-surface-raised" onClick={onClose}>
          <X size={16} aria-hidden />
        </button>
      </header>
      {(chat.error || actionError) && (
        <p role="alert" className="p-3 text-danger text-sm">
          {actionError || chat.error?.message}
          <button type="button" onClick={() => void chat.refresh()} className="ml-2 underline">再読み込み</button>
        </p>
      )}
      <p role="status" className="px-3 text-xs text-fg-muted">
        {chat.loading ? "スレッドを読み込んでいます…" : chat.connection === "open" ? "接続中" : "再接続しています…"}
      </p>
      <MessageList
        key={`thread:${roomId}:${threadRootId}`}
        messages={messages}
        onRetry={chat.retry}
        onReply={onReply}
        onCopy={async (mid) => {
          const m = messages.find((mm) => mm.id === mid);
          if (m) try { await navigator.clipboard?.writeText(m.body); } catch { /* clipboard may be unavailable */ }
        }}
        onLoadEarlier={chat.loadEarlier}
        loadingEarlier={chat.loadingEarlier}
        reachedStart={chat.reachedStart}
        onDelete={(mid) => { if (window.confirm("このメッセージを削除しますか？")) perform(chat.remove(mid)); }}
        onEdit={(mid) => { const original = messages.find((m) => m.id === mid); const body = window.prompt("メッセージを編集", original?.body); if (body?.trim()) perform(chat.edit(mid, body)); }}
        onReact={(mid) => perform(chat.react(mid))}
        onPin={(mid) => perform(chat.pin(mid))}
        onTaskify={(mid) => {
          const message = messages.find((m) => m.id === mid);
          if (message) perform(apiPost("/v1/tasks/promote-from-message", { message_id: mid, room_id: roomId, title: message.body.slice(0, 120) }));
        }}
      />
      {root ? (
      <MessageComposer
        key={`thread-composer:${roomId}:${threadRootId}`}
        draftKey={`ailechat:draft:${session?.user.id}:${session?.tenantId}:${roomId}:thread:${threadRootId}`}
        members={members}
        onSend={(body, extras) => {
          chat.send(body, { ...extras, parentId: replyingTo?.id ?? threadRootId });
          setReplyingTo(null);
        }}
        onTyping={chat.sendTyping}
        replyingTo={replyingTo ?? { id: threadRootId, author: root.authorName || root.authorId, preview: root.body.slice(0, 80) }}
        onCancelReply={() => setReplyingTo(null)}
      />
      ) : null}
      {onJump && highlightMessageId ? (
        <button type="button" className="sr-only" onClick={() => onJump(highlightMessageId)}>
          元のメッセージへ
        </button>
      ) : null}
    </aside>
  );
}
